import { useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideoFileOutlinedIcon from "@mui/icons-material/VideoFileOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../auth/authSlice";
import { useGetClassesQuery } from "../../classroom/classApiSlice";
import { useCreateTimelinePostMutation } from "../../../../app/communicationApiSlice";
import { useUploadFileMutation } from "../../../../app/utilsApiSlice";
import UserAvatar from "../../../common/UserAvatar";
import toast from "../../../../helpers/toast";

const MAX_VIDEO_SECONDS = 30;
const MAX_FILES = 4;

function validateVideoLength(file) {
  return new Promise((resolve) => {
    if (!file.type.startsWith("video/")) {
      resolve(true);
      return;
    }
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration <= MAX_VIDEO_SECONDS);
    };
    video.onerror = () => resolve(true); // allow if we can't check
    video.src = URL.createObjectURL(file);
  });
}

function FilePreview({ file, preview, onRemove }) {
  const isVideo = file.type.startsWith("video/");
  const isImage = file.type.startsWith("image/");

  return (
    <Box
      sx={{
        position: "relative",
        width: 80,
        height: 80,
        borderRadius: 1,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        flexShrink: 0,
      }}
    >
      {isImage && (
        <Box
          component="img"
          src={preview}
          alt={file.name}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
      {isVideo && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: "grey.900",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <VideoFileOutlinedIcon sx={{ color: "#fff", fontSize: 32 }} />
        </Box>
      )}
      {!isImage && !isVideo && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            p: 0.5,
          }}
        >
          <AttachFileOutlinedIcon sx={{ fontSize: 22, color: "text.secondary" }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.6rem",
              color: "text.secondary",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {file.name}
          </Typography>
        </Box>
      )}
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          position: "absolute",
          top: 2,
          right: 2,
          bgcolor: "rgba(0,0,0,0.55)",
          color: "#fff",
          p: 0.25,
          "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}

export default function PostComposer({ onPosted }) {
  const user = useSelector(selectCurrentUser);
  const roles = user?.roles?.map((r) => r?.role?.name).filter(Boolean) || [];
  const isAdmin = roles.includes("admin");
  const isTeacher = roles.includes("teacher");

  const { data: classesData } = useGetClassesQuery("all/mine", {
    skip: !isTeacher && !isAdmin,
  });
  const classes = classesData || [];
  const isClassManager = classes.length > 0;

  const [createPost, { isLoading: isPosting }] = useCreateTimelinePostMutation();
  const [uploadFile] = useUploadFileMutation();

  const [focused, setFocused] = useState(false);
  const [body, setBody] = useState("");
  const [audienceType, setAudienceType] = useState(
    isAdmin ? "parents" : "class_parents"
  );
  const [classroomId, setClassroomId] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]); // { file, preview, type: 'media' | 'attachment' }
  const [uploading, setUploading] = useState(false);
  const [eventDate, setEventDate] = useState(""); // YYYY-MM-DD or empty

  const mediaInputRef = useRef(null);
  const fileInputRef = useRef(null);

  if (!isAdmin && !isClassManager) return null;

  const displayName =
    `${user?.profile?.first_name || user?.first_name || ""} ${user?.profile?.last_name || user?.last_name || ""}`.trim() ||
    user?.username ||
    "You";

  const handleMediaSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_FILES - mediaFiles.length;
    if (remaining <= 0) return;
    const selected = files.slice(0, remaining);

    const validated = [];
    for (const file of selected) {
      const ok = await validateVideoLength(file);
      if (!ok) {
        toast.error(`"${file.name}" exceeds 30 seconds and was skipped.`);
        continue;
      }
      const preview = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
      validated.push({ file, preview, type: "media" });
    }
    setMediaFiles((prev) => [...prev, ...validated]);
    e.target.value = "";
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_FILES - mediaFiles.length;
    const selected = files.slice(0, remaining).map((file) => ({
      file,
      preview: null,
      type: "attachment",
    }));
    setMediaFiles((prev) => [...prev, ...selected]);
    e.target.value = "";
  };

  const removeFile = (index) => {
    setMediaFiles((prev) => {
      const next = [...prev];
      if (next[index]?.preview) URL.revokeObjectURL(next[index].preview);
      next.splice(index, 1);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!body.trim() && mediaFiles.length === 0) return;
    if (audienceType === "class_parents" && !classroomId) {
      toast.error("Please select a class.");
      return;
    }

    setUploading(true);
    const mediaUrls = [];
    const attachmentUrls = [];

    try {
      for (const item of mediaFiles) {
        const formData = new FormData();
        formData.append("file", item.file);
        const res = await uploadFile(formData).unwrap();
        if (res?.url) {
          if (item.type === "media") mediaUrls.push(res.url);
          else attachmentUrls.push(res.url);
        }
      }
    } catch {
      toast.error("Media upload failed. Please try again.");
      setUploading(false);
      return;
    }

    setUploading(false);

    try {
      await createPost({
        body: body.trim(),
        audience_type: audienceType,
        classroom_id: audienceType === "class_parents" ? classroomId : undefined,
        data: {
          ...(mediaUrls.length ? { media_urls: mediaUrls } : {}),
          ...(attachmentUrls.length ? { attachment_urls: attachmentUrls } : {}),
        },
        ...(eventDate ? {
          event_date: eventDate,
          // Browser's getTimezoneOffset returns minutes BEHIND UTC; negate for offset
          poster_utc_offset: -new Date().getTimezoneOffset(),
        } : {}),
      }).unwrap();

      setBody("");
      setMediaFiles([]);
      setEventDate("");
      setFocused(false);
      toast.success(eventDate ? "Event posted" : "Post shared");
      onPosted?.();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Could not share post");
    }
  };

  const isSubmitting = isPosting || uploading;
  const charCount = body.length;
  const CHAR_LIMIT = 2000;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <UserAvatar
            src={user?.photo || user?.profile?.photo}
            name={displayName}
            sx={{ width: 40, height: 40, flexShrink: 0, mt: 0.5 }}
          />
          <TextField
            multiline
            minRows={focused ? 4 : 2}
            fullWidth
            placeholder="What's happening?"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onFocus={() => setFocused(true)}
            inputProps={{ maxLength: CHAR_LIMIT }}
            variant="standard"
            sx={{
              "& .MuiInputBase-root::before": { display: "none" },
              "& .MuiInputBase-root::after": { display: "none" },
              "& textarea": { fontSize: "1rem", lineHeight: 1.6 },
            }}
          />
        </Stack>

        {/* Media previews */}
        {mediaFiles.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", pl: "52px" }}>
            {mediaFiles.map((item, i) => (
              <FilePreview
                key={i}
                file={item.file}
                preview={item.preview}
                onRemove={() => removeFile(i)}
              />
            ))}
          </Box>
        )}

        {(uploading) && <LinearProgress sx={{ borderRadius: 1 }} />}

        {focused && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pl: "52px",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Audience selector */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Audience</InputLabel>
                <Select
                  label="Audience"
                  value={audienceType}
                  onChange={(e) => setAudienceType(e.target.value)}
                >
                  {isAdmin && <MenuItem value="parents">All parents</MenuItem>}
                  {isAdmin && <MenuItem value="teachers">All teachers</MenuItem>}
                  {isClassManager && (
                    <MenuItem value="class_parents">Class parents</MenuItem>
                  )}
                </Select>
              </FormControl>

              {audienceType === "class_parents" && (
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Class</InputLabel>
                  <Select
                    label="Class"
                    value={classroomId}
                    onChange={(e) => setClassroomId(e.target.value)}
                  >
                    {classes.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Media toolbar */}
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                <Tooltip title="Add photos / videos">
                  <IconButton
                    size="small"
                    onClick={() => mediaInputRef.current?.click()}
                    disabled={mediaFiles.length >= MAX_FILES}
                  >
                    <ImageOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Attach file">
                  <IconButton
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={mediaFiles.length >= MAX_FILES}
                  >
                    <AttachFileOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={eventDate ? "Remove event date" : "Set event date"}>
                  <IconButton
                    size="small"
                    color={eventDate ? "primary" : "default"}
                    onClick={() => setEventDate("")}
                    sx={{ display: eventDate ? "inline-flex" : "none" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Set event date">
                  <IconButton
                    size="small"
                    color={eventDate ? "primary" : "default"}
                    component="label"
                  >
                    <EventOutlinedIcon fontSize="small" />
                    <input
                      type="date"
                      min={new Date().toISOString().slice(0, 10)}
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      style={{ position: "absolute", opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
              {eventDate && (
                <Chip
                  size="small"
                  icon={<EventOutlinedIcon fontSize="small" />}
                  label={new Date(eventDate + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                  onDelete={() => setEventDate("")}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Stack>

            {/* Right side: char count + submit */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="caption"
                color={charCount > CHAR_LIMIT * 0.9 ? "warning.main" : "text.disabled"}
              >
                {charCount}/{CHAR_LIMIT}
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SendOutlinedIcon />
                  )
                }
                disabled={
                  isSubmitting ||
                  (!body.trim() && mediaFiles.length === 0) ||
                  (audienceType === "class_parents" && !classroomId)
                }
                onClick={handleSubmit}
                sx={{ borderRadius: 5, textTransform: "none", px: 2.5 }}
              >
                Post
              </Button>
            </Stack>
          </Box>
        )}
      </Stack>

      {/* Hidden file inputs */}
      <input
        ref={mediaInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: "none" }}
        onChange={handleMediaSelect}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />
    </Paper>
  );
}
